import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  TextField,
  Banner,
  Badge,
  Icon,
  Divider,
  Spinner,
  ProgressBar,
} from '@shopify/polaris';
import { CheckCircleIcon, XCircleIcon } from '@shopify/polaris-icons';
import { Layout } from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import { authApi, billingApi, type SubscriptionData, type UsageLimitsData } from '../lib/api';
import { FEATURE_DISPLAY_NAMES, type PlanFeature } from '@analyzely/shared';

export function Account() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refreshUser } = useAuth();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Billing state
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [usage, setUsage] = useState<UsageLimitsData | null>(null);
  const [billingLoading, setBillingLoading] = useState(true);
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);

  // Fetch billing data function (reusable)
  const fetchBillingData = async (shouldSync = false) => {
    try {
      // Sync subscription state from Stripe first if requested
      // This ensures our database matches Stripe even if webhooks failed
      if (shouldSync) {
        try {
          await billingApi.syncSubscription();
        } catch (syncError) {
          console.error('Failed to sync subscription:', syncError);
          // Continue even if sync fails - we'll show cached data
        }
      }

      const [subRes, usageRes] = await Promise.all([
        billingApi.getSubscription(),
        billingApi.getUsage(),
      ]);

      if (subRes.success && subRes.data) {
        setSubscription(subRes.data);
      }
      if (usageRes.success && usageRes.data) {
        setUsage(usageRes.data);
      }
      return subRes.data;
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
      return null;
    } finally {
      setBillingLoading(false);
    }
  };

  // Check for checkout success from URL params and refetch with retry
  useEffect(() => {
    if (searchParams.get('checkout') === 'success') {
      setCheckoutSuccess(true);

      // Refetch billing data with retry to handle webhook delay
      // Webhook may take 1-3 seconds to process after Stripe redirect
      const refetchWithRetry = async (attempts = 0, previousPlanId?: string) => {
        const data = await fetchBillingData();

        // If this is first attempt, store current plan for comparison
        if (attempts === 0) {
          previousPlanId = data?.plan?.id;
        }

        // If plan hasn't updated yet and we have retries left, wait and try again
        // This handles the race condition where webhook hasn't processed yet
        if (attempts < 5 && data?.plan?.id === previousPlanId && previousPlanId === 'plan_free') {
          setTimeout(() => refetchWithRetry(attempts + 1, previousPlanId), 1500);
        }
      };

      // Start refetch after a short delay to give webhook time to process
      setTimeout(() => refetchWithRetry(), 1000);
    }
  }, [searchParams]);

  // Initial fetch billing data on mount - sync with Stripe first
  useEffect(() => {
    fetchBillingData(true); // Pass true to sync with Stripe
  }, []);

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      setFirstName(user.first_name || '');
      setLastName(user.last_name || '');
    }
  }, [user]);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveSuccess(false);
    setSaveError(null);

    try {
      await authApi.updateProfile({
        first_name: firstName,
        last_name: lastName,
      });
      await refreshUser();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to update profile:', error);
      setSaveError(error instanceof Error ? error.message : 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const response = await billingApi.getPortalUrl();
      if (response.success && response.data.portal_url) {
        window.location.href = response.data.portal_url;
      }
    } catch (error) {
      console.error('Portal error:', error);
      navigate('/pricing');
    }
  };

  // Get user initials for avatar
  const getInitials = () => {
    const first = firstName || user?.first_name || '';
    const last = lastName || user?.last_name || '';
    if (first && last) {
      return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
    }
    if (first) {
      return first.charAt(0).toUpperCase();
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusBadge = () => {
    if (!subscription) return { text: 'Active', tone: 'success' as const };

    // Check if subscription is scheduled for cancellation
    if (subscription.cancel_at_period_end && subscription.status === 'active') {
      return { text: 'Cancelling', tone: 'warning' as const };
    }

    switch (subscription.status) {
      case 'trialing':
        return { text: `Trial (${subscription.trial_days_remaining || 0} days left)`, tone: 'warning' as const };
      case 'past_due':
        return { text: 'Past Due', tone: 'critical' as const };
      case 'cancelled':
        return { text: 'Cancelled', tone: 'info' as const };
      default:
        return { text: 'Active', tone: 'success' as const };
    }
  };

  const statusBadge = getStatusBadge();
  const planName = subscription?.plan?.name
    ? subscription.plan.name.charAt(0).toUpperCase() + subscription.plan.name.slice(1)
    : 'Free';

  // Calculate usage percentage
  const usagePercent = usage && usage.sites.limit > 0
    ? Math.min(100, (usage.sites.used / usage.sites.limit) * 100)
    : 0;

  return (
    <Layout>
      <BlockStack gap="600">
        {/* Checkout success banner */}
        {checkoutSuccess && (
          <Banner tone="success" onDismiss={() => setCheckoutSuccess(false)}>
            Your subscription has been updated successfully!
          </Banner>
        )}

        {/* Cancellation warning banner */}
        {subscription?.cancel_at_period_end && subscription.status === 'active' && (
          <Banner tone="warning">
            <BlockStack gap="200">
              <Text as="p" variant="bodyMd" fontWeight="semibold">
                Your subscription is scheduled for cancellation
              </Text>
              <Text as="p" variant="bodySm">
                Your {planName} plan will be cancelled on{' '}
                {subscription.current_period_end
                  ? formatDate(subscription.current_period_end)
                  : subscription.trial_ends_at
                    ? formatDate(subscription.trial_ends_at)
                    : 'the end of your billing period'}.
                You'll continue to have access to all features until then.
                After cancellation, you'll be downgraded to the Free plan.
              </Text>
              <InlineStack gap="200">
                <Button onClick={handleManageSubscription}>
                  Keep My Subscription
                </Button>
                <Button variant="plain" onClick={() => navigate('/pricing')}>
                  View Plans
                </Button>
              </InlineStack>
            </BlockStack>
          </Banner>
        )}

        {/* Header */}
        <BlockStack gap="200">
          <Text as="h1" variant="headingLg" fontWeight="bold">
            Account Settings
          </Text>
          <Text as="p" tone="subdued">
            Manage your profile and account preferences
          </Text>
        </BlockStack>

        {/* Profile Card */}
        <Card>
          <BlockStack gap="500">
            <Text as="h2" variant="headingMd" fontWeight="bold">
              Profile
            </Text>

            {/* Avatar and Info */}
            <InlineStack gap="400" blockAlign="center">
              <div
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  backgroundColor: '#5c6ac4',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '24px',
                }}
              >
                {getInitials()}
              </div>
              <BlockStack gap="100">
                <Text as="p" variant="headingSm" fontWeight="semibold">
                  {firstName || lastName
                    ? `${firstName} ${lastName}`.trim()
                    : user?.email?.split('@')[0] || 'User'}
                </Text>
                <Text as="p" tone="subdued">
                  {user?.email}
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Member since {formatDate(user?.created_at)}
                </Text>
              </BlockStack>
            </InlineStack>

            <Divider />

            {/* Edit Form */}
            <BlockStack gap="400">
              <TextField
                label="First Name"
                value={firstName}
                onChange={setFirstName}
                autoComplete="given-name"
              />
              <TextField
                label="Last Name"
                value={lastName}
                onChange={setLastName}
                autoComplete="family-name"
              />

              {saveSuccess && (
                <Banner tone="success" onDismiss={() => setSaveSuccess(false)}>
                  Profile updated successfully!
                </Banner>
              )}

              {saveError && (
                <Banner tone="critical" onDismiss={() => setSaveError(null)}>
                  {saveError}
                </Banner>
              )}

              <InlineStack>
                <Button variant="primary" loading={isSaving} onClick={handleSave}>
                  Save Changes
                </Button>
              </InlineStack>
            </BlockStack>
          </BlockStack>
        </Card>

        {/* Subscription Card */}
        <Card>
          <BlockStack gap="400">
            <InlineStack align="space-between" blockAlign="center">
              <Text as="h2" variant="headingMd" fontWeight="bold">
                Subscription
              </Text>
              {subscription?.stripe_customer_id ? (
                <Button onClick={handleManageSubscription}>Manage Subscription</Button>
              ) : (
                <Button onClick={() => navigate('/pricing')}>Upgrade</Button>
              )}
            </InlineStack>

            {billingLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                <Spinner size="small" />
              </div>
            ) : (
              <BlockStack gap="400">
                {/* Plan Info */}
                <div
                  style={{
                    backgroundColor: '#f6f6f7',
                    borderRadius: '8px',
                    padding: '16px',
                    border: '1px solid #e1e3e5',
                  }}
                >
                  <InlineStack align="space-between" blockAlign="center">
                    <BlockStack gap="100">
                      <InlineStack gap="200" blockAlign="center">
                        <Text as="span" variant="headingSm" fontWeight="bold">
                          {planName} Plan
                        </Text>
                        <Badge tone={statusBadge.tone}>{statusBadge.text}</Badge>
                      </InlineStack>
                      {(subscription?.current_period_end || subscription?.trial_ends_at) && subscription.status !== 'cancelled' && (
                        <Text as="p" variant="bodySm" tone={subscription.cancel_at_period_end ? 'caution' : 'subdued'}>
                          {subscription.status === 'trialing'
                            ? 'Trial ends'
                            : subscription.cancel_at_period_end
                              ? 'Cancels'
                              : 'Renews'} on{' '}
                          {formatDate(subscription.current_period_end || subscription.trial_ends_at)}
                        </Text>
                      )}
                      {subscription?.billing_interval && subscription.plan.monthly_price > 0 && (
                        <Text as="p" variant="bodySm" tone="subdued">
                          ${subscription.billing_interval === 'yearly'
                            ? subscription.plan.yearly_price / 100
                            : subscription.plan.monthly_price / 100
                          }/{subscription.billing_interval === 'yearly' ? 'year' : 'month'}
                        </Text>
                      )}
                    </BlockStack>
                  </InlineStack>
                </div>

                {/* Usage & Limits */}
                {usage && (
                  <div
                    style={{
                      backgroundColor: '#ffffff',
                      borderRadius: '8px',
                      padding: '16px',
                      border: '1px solid #e1e3e5',
                    }}
                  >
                    <BlockStack gap="400">
                      <Text as="span" variant="bodyMd" fontWeight="semibold">
                        Usage & Limits
                      </Text>

                      {/* Sites usage */}
                      <BlockStack gap="200">
                        <InlineStack align="space-between">
                          <Text as="span" variant="bodySm">
                            Sites
                          </Text>
                          <Text as="span" variant="bodySm" fontWeight="semibold">
                            {usage.sites.used} / {usage.sites.limit === -1 ? 'Unlimited' : usage.sites.limit}
                          </Text>
                        </InlineStack>
                        {usage.sites.limit !== -1 && (
                          <ProgressBar
                            progress={usagePercent}
                            tone={usagePercent >= 90 ? 'critical' : 'primary'}
                            size="small"
                          />
                        )}
                        {!usage.sites.canAddMore && usage.sites.limit !== -1 && (
                          <Banner tone="warning">
                            You've reached your site limit.{' '}
                            <button
                              onClick={() => navigate('/pricing')}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#2c6ecb',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                padding: 0,
                                font: 'inherit',
                              }}
                            >
                              Upgrade to add more sites
                            </button>
                          </Banner>
                        )}
                      </BlockStack>

                      {/* Data retention */}
                      <InlineStack align="space-between">
                        <Text as="span" variant="bodySm">
                          Data Retention
                        </Text>
                        <Text as="span" variant="bodySm" fontWeight="semibold">
                          {usage.dataRetentionDays === -1 ? 'Unlimited' : `${usage.dataRetentionDays} days`}
                        </Text>
                      </InlineStack>

                      <Divider />

                      {/* Plan Features */}
                      <Text as="span" variant="bodyMd" fontWeight="semibold">
                        Plan Features
                      </Text>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(2, 1fr)',
                          gap: '8px',
                        }}
                      >
                        {usage.features?.map((feature) => (
                          <InlineStack key={feature} gap="100" blockAlign="center">
                            <div
                              style={{
                                width: '16px',
                                height: '16px',
                                backgroundColor: '#b4fed3',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <Icon source={CheckCircleIcon} tone="success" />
                            </div>
                            <Text as="span" variant="bodySm">
                              {FEATURE_DISPLAY_NAMES[feature as PlanFeature] || feature}
                            </Text>
                          </InlineStack>
                        ))}
                      </div>
                    </BlockStack>
                  </div>
                )}

                {/* Grandfathered notice */}
                {subscription?.is_grandfathered && (
                  <Banner tone="info">
                    You have a grandfathered account. You can keep your existing{' '}
                    {subscription.grandfathered_sites_count} sites on the Free plan.
                  </Banner>
                )}
              </BlockStack>
            )}
          </BlockStack>
        </Card>

        {/* Connected Services Card */}
        <Card>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd" fontWeight="bold">
              Connected Services
            </Text>

            <Text as="p" tone="subdued" variant="bodySm">
              Services connected to your Analyzely account
            </Text>

            {/* Webflow Connection */}
            <div
              style={{
                backgroundColor: '#f6f6f7',
                borderRadius: '8px',
                padding: '16px',
                border: '1px solid #e1e3e5',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      backgroundColor: '#146EF5',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <span style={{ color: 'white', fontWeight: 'bold', fontSize: '18px' }}>W</span>
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Text as="span" variant="bodyMd" fontWeight="semibold">
                        Webflow
                      </Text>
                      <Badge tone="success">Connected</Badge>
                    </div>
                    <Text as="p" variant="bodySm" tone="subdued">
                      Access to your Webflow sites
                    </Text>
                  </div>
                </div>
                <div
                  style={{
                    width: '24px',
                    height: '24px',
                    backgroundColor: '#b4fed3',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon source={CheckCircleIcon} tone="success" />
                </div>
              </div>
            </div>

            {/* Google Connection - shows as info, managed per-site */}
            <div
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '8px',
                padding: '16px',
                border: '1px solid #e1e3e5',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      backgroundColor: '#ffffff',
                      border: '1px solid #e1e3e5',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <span style={{ fontSize: '18px', fontWeight: 500, color: '#5c5f62' }}>G</span>
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Text as="span" variant="bodyMd" fontWeight="semibold">
                        Google Analytics
                      </Text>
                      <Badge tone="info">Per Site</Badge>
                    </div>
                    <Text as="p" variant="bodySm" tone="subdued">
                      Connect Google on each site's setup page
                    </Text>
                  </div>
                </div>
                <div
                  style={{
                    width: '24px',
                    height: '24px',
                    backgroundColor: '#f6f6f7',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon source={XCircleIcon} tone="subdued" />
                </div>
              </div>
            </div>
          </BlockStack>
        </Card>
      </BlockStack>
    </Layout>
  );
}
